import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, TrendingUp, TrendingDown, BarChart3, Clock, RefreshCw, Calendar } from 'lucide-react';
import { peopleCountingService } from '../lib/supabaseClient';
import { useTheme } from '../App';
import { formatDateDisplay, formatDateForInput, getTodayString } from '../utils/dateUtils';

const PeopleCountingDashboard = ({ showNotification, isActive = true }) => {
  const { t } = useTheme();
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Check if viewing today
  const isToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected.getTime() === today.getTime();
  }, [selectedDate]);

  const handleDateSelect = (dateString) => {
    setSelectedDate(new Date(dateString));
    setShowDatePicker(false);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setShowDatePicker(false);
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bereken werkelijke piekaanwezigheid en huidig aantal binnen
  // via lopend cumulatief totaal over alle uurrecords (gesorteerd op tijd)
  const calcInsideStats = (data) => {
    const sorted = [...data].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    let running = 0;
    let peak = 0;
    sorted.forEach(h => {
      running += (h.entered_total || 0) - (h.exited_total || 0);
      if (running > peak) peak = running;
    });
    return {
      current: Math.max(0, running),
      peak: Math.max(0, peak),
    };
  };

  const loadData = useCallback(async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      if (isToday()) {
        // Load dashboard stats for today
        const statsData = await peopleCountingService.getDashboardStats();

        // Load today's hourly data
        const today = await peopleCountingService.getTodayHourly();
        setHourlyData(today);

        // Bereken werkelijk huidig aantal binnen en piek via cumulatief totaal
        const insideStats = calcInsideStats(today);
        setStats({
          ...statsData,
          current_inside: insideStats.current,
          today: {
            ...statsData.today,
            peak_inside: insideStats.peak,
          },
        });
      } else {
        // Load historical data for selected date
        const dayStart = new Date(selectedDate);
        dayStart.setHours(5, 0, 0, 0); // Event day starts at 5:00 AM
        const dayEnd = new Date(selectedDate);
        dayEnd.setDate(dayEnd.getDate() + 1);
        dayEnd.setHours(4, 59, 59, 999); // Until 4:59 AM next day

        const historicalData = await peopleCountingService.getHistorical(
          dayStart.toISOString(),
          dayEnd.toISOString(),
          'Hour'
        );
        setHourlyData(historicalData);

        // Bereken werkelijke piek via cumulatief lopend totaal
        const insideStats = calcInsideStats(historicalData);
        const totalEntered = historicalData.reduce((sum, h) => sum + (h.entered_total || 0), 0);
        const totalExited = historicalData.reduce((sum, h) => sum + (h.exited_total || 0), 0);

        setStats({
          current_hour: null,
          current_inside: insideStats.current,
          today: {
            entered: totalEntered,
            exited: totalExited,
            peak_inside: insideStats.peak,
          },
          last_sync: null,
          total_records: historicalData.length
        });
      }

    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Fout bij laden van data: ' + error.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, isToday, showNotification]);

  // Load data on mount and when date changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds (only for today and when page is active)
  useEffect(() => {
    if (autoRefresh && isActive && isToday()) {
      const interval = setInterval(() => {
        loadData(true);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, isActive, isToday, loadData]);

  const handleManualRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zf-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold ${t.text}`}>Personen Telling</h1>
          <p className={`${t.textSecondary} mt-1`}>
            {isToday() ? 'Real-time bezoekers monitoring' : `Historische data voor ${formatDateDisplay(selectedDate)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Ververs
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 border ${t.border} ${autoRefresh ? 'bg-zf-accent text-white' : `${t.bgSecondary} ${t.text}`} transition-colors`}
          >
            <Clock className="w-4 h-4" />
            Auto ({autoRefresh ? 'aan' : 'uit'})
          </button>
        </div>
      </div>

      {/* Date Selection */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${t.text}`}>Datum selecteren</h2>
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${t.border} ${t.hover} transition-colors`}
            >
              <Calendar className={`w-5 h-5 ${t.textSecondary}`} />
              <span className={`font-medium ${t.text}`}>{formatDateDisplay(selectedDate)}</span>
            </button>

            {showDatePicker && (
              <div className={`absolute right-0 top-full mt-2 z-50 ${t.card} rounded-xl shadow-xl border p-4`}>
                <input
                  type="date"
                  value={formatDateForInput(selectedDate)}
                  max={getTodayString()}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  className={`${t.input} border rounded-lg px-4 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={goToToday}
                    className="flex-1 bg-zf-accent text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-zf-accent/80 transition-colors"
                  >
                    Vandaag
                  </button>
                  <button
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      setSelectedDate(yesterday);
                      setShowDatePicker(false);
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${t.border} ${t.text} ${t.hover} transition-colors`}
                  >
                    Gisteren
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Hour Stats (only for today) */}
      {isToday() && (
        <div>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Laatste Uur</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Inkomend"
              value={stats?.current_hour?.entered || 0}
              icon={<TrendingUp className="w-6 h-6" />}
              color="success"
            />
            <StatCard
              title="Uitgaand"
              value={stats?.current_hour?.exited || 0}
              icon={<TrendingDown className="w-6 h-6" />}
              color="error"
            />
            <StatCard
              title="Netto"
              value={(stats?.current_hour?.entered || 0) - (stats?.current_hour?.exited || 0)}
              icon={<BarChart3 className="w-6 h-6" />}
              color="accent"
            />
            <StatCard
              title="Momenteel Binnen"
              value={stats?.current_inside || 0}
              icon={<Users className="w-6 h-6" />}
              color="accent2"
            />
          </div>
        </div>
      )}

      {/* Day Stats */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>
          {isToday() ? 'Vandaag Totaal' : 'Dag Totaal'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Totaal Inkomend"
            value={stats?.today?.entered || 0}
            icon={<TrendingUp className="w-6 h-6" />}
            color="success"
          />
          <StatCard
            title="Totaal Uitgaand"
            value={stats?.today?.exited || 0}
            icon={<TrendingDown className="w-6 h-6" />}
            color="error"
          />
          <StatCard
            title="Netto Verandering"
            value={(stats?.today?.entered || 0) - (stats?.today?.exited || 0)}
            icon={<BarChart3 className="w-6 h-6" />}
            color="accent"
          />
          <StatCard
            title="Peak Binnen"
            value={stats?.today?.peak_inside || 0}
            icon={<Users className="w-6 h-6" />}
            color="accent2"
          />
        </div>
      </div>

      {/* Hourly Chart (Simple Bar Chart) */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>
          Uuroverzicht {formatDateDisplay(selectedDate)}
        </h2>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {/* Legend */}
          <div className="flex gap-6 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className={`text-sm ${t.textSecondary}`}>Inkomend</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className={`text-sm ${t.textSecondary}`}>Uitgaand</span>
            </div>
          </div>

          {hourlyData.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                const maxValue = Math.max(...hourlyData.map(h => Math.max(h.entered_total || 0, h.exited_total || 0)), 1);
                return hourlyData.map((hour, index) => {
                const enteredPercent = maxValue > 0 ? ((hour.entered_total || 0) / maxValue) * 100 : 0;
                const exitedPercent = maxValue > 0 ? ((hour.exited_total || 0) / maxValue) * 100 : 0;
                const hourLabel = new Date(hour.start_time).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

                return (
                  <div key={index} className="flex items-center gap-4">
                    <span className={`${t.textSecondary} text-sm w-16`}>{hourLabel}</span>
                    <div className="flex-1 space-y-1">
                      {/* Entered bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-green-500 h-full transition-all duration-300"
                            style={{ width: `${enteredPercent}%` }}
                          />
                        </div>
                        <span className={`${t.text} text-sm w-12 text-right`}>{hour.entered_total || 0}</span>
                      </div>
                      {/* Exited bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-red-500 h-full transition-all duration-300"
                            style={{ width: `${exitedPercent}%` }}
                          />
                        </div>
                        <span className={`${t.text} text-sm w-12 text-right`}>{hour.exited_total || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
            </div>
          ) : (
            <p className={`${t.textSecondary} text-center py-8`}>
              Geen data beschikbaar voor {formatDateDisplay(selectedDate)}
            </p>
          )}
        </div>
      </div>

      {/* Info Panel */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`${t.textSecondary} text-sm`}>Laatste synchronisatie</p>
            <p className={`${t.text} font-medium`}>
              {stats?.last_sync
                ? new Date(stats.last_sync).toLocaleString('nl-NL')
                : 'Nog geen synchronisatie'}
            </p>
          </div>
          <div>
            <p className={`${t.textSecondary} text-sm text-right`}>Totaal records</p>
            <p className={`${t.text} font-medium text-right`}>
              {stats?.total_records?.toLocaleString('nl-NL') || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// StatCard component (matches existing pattern from kenteken scanner)
const StatCard = ({ title, value, icon, color }) => {
  const { t } = useTheme();

  const colorClasses = {
    success: 'text-green-500',
    error: 'text-red-500',
    accent: 'text-zf-accent',
    accent2: 'text-blue-500',
  };

  return (
    <div className={`${t.card} rounded-xl shadow-lg p-6 border transition-all hover:shadow-xl`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`${t.textSecondary} text-sm mb-1`}>{title}</p>
          <p className={`${t.text} text-3xl font-bold`}>{value.toLocaleString('nl-NL')}</p>
        </div>
        <div className={colorClasses[color]}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default PeopleCountingDashboard;
